const QUIZ_METAFIELD_FILTER_PREFIX = 'filter.p.m.';

// Filename of the section the availability requests render. The Section
// Rendering API resolves filenames against the current URL's context, so the
// section never needs to be added to the collection's template.
const QUIZ_AVAILABILITY_SECTION = 'quiz-availability';

function quizFilterKey(param) {
  const normalized = param?.trim();

  if (!normalized) {
    return null;
  }

  return normalized.startsWith('filter.') ? normalized : `${QUIZ_METAFIELD_FILTER_PREFIX}${normalized}`;
}

function normalizeFilterValue(value) {
  return value?.trim().toLowerCase() || '';
}

function quizOptionSlot(option) {
  const container = option.closest?.('.quiz__options');

  if (!container) {
    return option;
  }

  let slot = option;

  while (slot.parentElement && slot.parentElement !== container) {
    slot = slot.parentElement;
  }

  return slot;
}

function setQuizOptionHidden(option, hidden) {
  const slot = quizOptionSlot(option);

  option.toggleAttribute('hidden', hidden);

  if (slot !== option) {
    slot.toggleAttribute('hidden', hidden);
  }
}

if (!customElements.get('quiz-funnel')) {
  class QuizFunnel extends HTMLElement {
    constructor() {
      super();

      this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
      this.backButton = this.querySelector('[data-quiz-back]');
      this.collectionUrl = this.dataset.collectionUrl;

      this.checksAvailability =
        this.dataset.checkAvailability === 'true' && Boolean(this.collectionUrl);

      this.opensSingleResult =
        this.dataset.autoOpenSingle === 'true' && Boolean(this.collectionUrl);

      this.availabilityRequests = new Map();

      this.answers = [];
      this.currentStep = 0;
      this.prefilledSteps = new Set();
      this.navigationId = 0;

      this.addEventListener('click', this.onClick.bind(this));

      if (this.dataset.prefillFromUrl === 'true') {
        this.applyUrlAnswers();
      }

      this.goToStep(this.nextStep(-1), 1);
    }

    applyUrlAnswers() {
      const params = new URLSearchParams(window.location.search);

      this.steps.forEach((step, index) => {
        const param = step.dataset.filterParam?.trim();

        if (!param) {
          return;
        }

        const value = params.get(param) || params.get(param.split('.').pop());

        if (!value) {
          return;
        }

        const options = Array.from(step.querySelectorAll('[data-quiz-option]'));
        const option = options.find(
          candidate => normalizeFilterValue(candidate.dataset.filterValue) === normalizeFilterValue(value)
        );

        if (!option) {
          return;
        }

        this.saveAnswer(
          index,
          param,
          option.dataset.filterValue,
          option.dataset.alsoShowValue,
          option.dataset.alsoShowHeading
        );

        this.prefilledSteps.add(index);
      });
    }

    nextStep(from) {
      for (let index = from + 1; index < this.steps.length; index += 1) {
        if (!this.prefilledSteps.has(index)) {
          return index;
        }
      }

      return -1;
    }

    previousStep(from) {
      for (let index = from - 1; index >= 0; index -= 1) {
        if (!this.prefilledSteps.has(index)) {
          return index;
        }
      }

      return -1;
    }

    onClick(event) {
      const option = event.target.closest('[data-quiz-option]');

      if (option) {
        this.selectOption(option);

        return;
      }

      if (event.target.closest('[data-quiz-back]')) {
        this.back();
      }
    }

    selectOption(option) {
      const step = option.closest('[data-quiz-step]');

      this.saveAnswer(
        this.currentStep,
        step?.dataset.filterParam,
        option.dataset.filterValue,
        option.dataset.alsoShowValue,
        option.dataset.alsoShowHeading
      );

      this.goToStep(this.nextStep(this.currentStep), 1);
    }

    saveAnswer(index, param, value, alsoShow, alsoHeading) {
      const normalizedParam = param?.trim();
      const normalizedValue = value?.trim();
      const normalizedAlsoShow = alsoShow?.trim();
      const normalizedAlsoHeading = alsoHeading?.trim();

      this.answers[index] = normalizedParam && normalizedValue
        ? {
            param: normalizedParam,
            value: normalizedValue,
            alsoShow: normalizedAlsoShow || null,
            alsoHeading: normalizedAlsoHeading || null,
          }
        : null;
    }

    clearAnswer(index) {
      this.answers[index] = null;
    }

    // Answers only ever narrow the results, so anything recorded after the step
    // being returned to is no longer valid. Prefilled answers come from the
    // link rather than from a question, so they survive.
    clearAnswersFrom(index) {
      for (let step = index; step < this.answers.length; step += 1) {
        if (!this.prefilledSteps.has(step)) {
          this.clearAnswer(step);
        }
      }
    }

    back() {
      const previousStep = this.previousStep(this.currentStep);

      if (previousStep === -1) {
        return;
      }

      this.clearAnswersFrom(previousStep);
      this.goToStep(previousStep, -1);
    }

    async goToStep(i, direction) {
      // Availability lookups are awaited, so a second answer can be given while
      // the first is still resolving. Only the newest navigation may land.
      const navigation = this.navigationId + 1;

      this.navigationId = navigation;
      this.setAttribute('aria-busy', 'true');

      let cursor = i;

      while (cursor !== -1) {
        const availableOptions = await this.applyAvailability(cursor, navigation);

        if (navigation !== this.navigationId) {
          return;
        }

        if (availableOptions > 0) {
          break;
        }

        cursor = direction === -1 ? this.previousStep(cursor) : this.nextStep(cursor);
      }

      this.removeAttribute('aria-busy');

      // Going back can skip past questions with nothing left to choose, landing
      // earlier than back() cleared from. Whatever was answered from there on
      // no longer applies.
      if (direction === -1 && cursor !== -1) {
        this.clearAnswersFrom(cursor);
      }

      if (cursor === -1) {
        // Nothing left to ask going forward means every answer is in. Going
        // backwards there is simply nothing to return to, so stay put.
        if (direction !== -1) {
          this.finish();
        }

        return;
      }

      this.showStep(cursor);
      this.prefetchNextStep(cursor);
    }

    showStep(index) {
      this.currentStep = index;

      this.steps.forEach((step, stepIndex) => {
        step.toggleAttribute('hidden', stepIndex !== index);
      });

      if (this.backButton) {
        this.backButton.toggleAttribute('hidden', this.previousStep(index) === -1);
      }

      this.applyPrices(index);
    }

    // Fills in the live price of each visible option on `index` that has no
    // price override: the first product matching every answer so far plus that
    // option, marked down by its compare-at price or an automatic discount.
    // Fails closed: a price that can't be resolved stays hidden.
    applyPrices(index) {
      const step = this.steps[index];

      if (!step || !this.collectionUrl) {
        return;
      }

      const filterKey = quizFilterKey(step.dataset.filterParam);

      if (!filterKey) {
        return;
      }

      const pending = [];

      step.querySelectorAll('[data-quiz-option]:not([hidden])').forEach(option => {
        const priceElement = option.querySelector('[data-quiz-price]');
        const value = option.dataset.filterValue?.trim();

        if (!priceElement || !value) {
          return;
        }

        // Answers at and after `index` are cleared before it is shown, so
        // these are the answers so far, prefilled ones included.
        const params = this.filtersBefore(this.steps.length);

        params.append(filterKey, value);
        params.sort();

        // Going back can change the answers this card is priced against, and
        // a slower, older lookup must not overwrite the current one.
        const priceKey = params.toString();

        if (priceElement.dataset.quizPriceKey === priceKey) {
          return;
        }

        priceElement.dataset.quizPriceKey = priceKey;
        priceElement.hidden = true;
        pending.push({ priceElement, priceKey, params });
      });

      if (!pending.length) {
        return;
      }

      Promise.all(pending.map(({ params }) => this.requestAvailability(params))).then(async availabilities => {
        const products = availabilities.map(availability => availability?.first_product || null);
        // Without the discount lookup, cards still show compare-at sale prices.
        const discountedPrices =
          (await window.storefrontDiscounts?.requestDiscountedPrices(
            products.filter(Boolean).map(product => product.variant_id)
          )) || new Map();

        pending.forEach(({ priceElement, priceKey }, i) => {
          if (priceElement.dataset.quizPriceKey !== priceKey) {
            return;
          }

          const product = products[i];

          this.renderPrice(
            priceElement,
            product ? this.salePrice(product, discountedPrices.get(product.variant_id)) : null
          );
        });
      });
    }

    salePrice(product, discountedPrice) {
      return {
        regular: Math.max(product.price, product.compare_at_price || 0),
        sale: Math.min(product.price, discountedPrice ?? product.price),
      };
    }

    renderPrice(element, price) {
      if (!price) {
        element.hidden = true;
        element.replaceChildren();

        return;
      }

      const format = amount => formatPrice(amount, this.dataset.moneyFormat);
      const onSale = price.sale < price.regular;

      element.classList.toggle('quiz-card__price--on-sale', onSale);

      if (!onSale) {
        element.replaceChildren(format(price.regular));
        element.hidden = false;

        return;
      }

      const label = text => {
        const span = document.createElement('span');

        span.className = 'visually-hidden';
        span.textContent = text;

        return span;
      };

      const regular = document.createElement('s');
      const sale = document.createElement('ins');

      regular.className = 'quiz-card__price-compare';
      regular.textContent = format(price.regular);
      sale.className = 'quiz-card__price-sale';
      sale.textContent = format(price.sale);

      element.replaceChildren(
        label(this.dataset.labelPriceRegular),
        regular,
        label(this.dataset.labelPriceSale),
        sale
      );
      element.hidden = false;
    }

    // Hides the options of `index` that would return no products alongside the
    // answers already given, and reports how many are left. Fails open: if the
    // counts can't be resolved, every option stays visible.
    async applyAvailability(index, navigation) {
      const step = this.steps[index];

      if (!step) {
        return 0;
      }

      const options = Array.from(step.querySelectorAll('[data-quiz-option]'));

      if (!this.checksAvailability) {
        return options.length;
      }

      const availability = await this.requestAvailability(this.filtersBefore(index));

      // A newer navigation may have started while the counts were in flight.
      // Its availability owns the DOM now, so leave the options untouched.
      if (navigation !== this.navigationId) {
        return 0;
      }
      const filterKey = quizFilterKey(step.dataset.filterParam);
      const counts = filterKey ? availability?.filters?.[filterKey] : null;

      if (!counts) {
        options.forEach(option => setQuizOptionHidden(option, false));

        return options.length;
      }

      const countsByValue = new Map(
        Object.entries(counts).map(([value, count]) => [normalizeFilterValue(value), count])
      );

      let visibleOptions = 0;

      options.forEach(option => {
        const count = countsByValue.get(normalizeFilterValue(option.dataset.filterValue));
        const isAvailable = Boolean(count);

        setQuizOptionHidden(option, !isAvailable);

        if (isAvailable) {
          visibleOptions += 1;
        }
      });

      return visibleOptions;
    }

    filtersBefore(index) {
      const params = new URLSearchParams();

      for (let step = 0; step < index; step += 1) {
        const answer = this.answers[step];
        const filterKey = answer ? quizFilterKey(answer.param) : null;

        if (filterKey) {
          params.append(filterKey, answer.value);
        }
      }

      return params;
    }

    // Warms the cache for whichever step comes next, once per option the
    // customer could pick, so advancing doesn't wait on the network. On the
    // last step it warms the full-answer counts finish() reads instead.
    prefetchNextStep(index) {
      const next = this.nextStep(index);
      const step = this.steps[index];

      if (!step) {
        return;
      }

      if (next === -1 ? !this.opensSingleResult : !this.checksAvailability) {
        return;
      }

      const filterKey = quizFilterKey(step.dataset.filterParam);

      if (!filterKey) {
        return;
      }

      const upTo = next === -1 ? this.steps.length : next;

      Array.from(step.querySelectorAll('[data-quiz-option]:not([hidden])')).forEach(option => {
        const value = option.dataset.filterValue?.trim();

        if (!value) {
          return;
        }

        const params = this.filtersBefore(upTo);

        params.append(filterKey, value);
        this.requestAvailability(params);
      });
    }

    requestAvailability(params) {
      params.sort();

      const cacheKey = params.toString();

      if (this.availabilityRequests.has(cacheKey)) {
        return this.availabilityRequests.get(cacheKey);
      }

      const url = new URL(this.collectionUrl, window.location.origin);

      params.forEach((value, key) => url.searchParams.append(key, value));
      url.searchParams.set('section_id', QUIZ_AVAILABILITY_SECTION);

      const request = fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error('Quiz availability request failed');
          }

          return response.text();
        })
        .then(html => {
          const payload = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-quiz-availability]');

          return payload ? JSON.parse(payload.textContent) : null;
        })
        .catch(() => null);

      this.availabilityRequests.set(cacheKey, request);

      return request;
    }

    buildResultsUrl() {
      const url = new URL(this.collectionUrl, window.location.origin);

      this.answers.forEach(answer => {
        if (!answer) {
          return;
        }

        url.searchParams.append(quizFilterKey(answer.param), answer.value);

        if (!answer.alsoShow) {
          return;
        }

        url.searchParams.set('also_show', answer.alsoShow);
        url.searchParams.set('also_param', answer.param);
        url.searchParams.set('also_heading', answer.alsoHeading || answer.alsoShow);
      });

      const fallbackParam = this.dataset.fallbackParam?.trim();
      const fallbackValue = this.dataset.fallbackValue?.trim();
      const fallbackHeading = this.dataset.fallbackHeading?.trim();

      if (fallbackParam && fallbackValue) {
        url.searchParams.set('fallback_param', fallbackParam);
        url.searchParams.set('fallback_value', fallbackValue);
        url.searchParams.set('fallback_heading', fallbackHeading || fallbackValue);
      }

      return url;
    }

    // The product page URL when the answers match exactly one product, so
    // finish() can open it directly instead of a one-product results page.
    // Carries ?openCustomizer=1 so the PDP's Spectrum bridge launches the
    // customizer on load, same as the results page's Customize links.
    // Resolves to null when an answer adds an "also show" grid (the results
    // page would offer more than that one glove) or when the count can't be
    // resolved.
    async singleResultUrl() {
      if (this.answers.some(answer => answer?.alsoShow)) {
        return null;
      }

      const availability = await this.requestAvailability(this.filtersBefore(this.steps.length));

      if (availability?.products_count !== 1 || !availability.first_product_url) {
        return null;
      }

      const url = new URL(availability.first_product_url, window.location.origin);

      url.searchParams.set('openCustomizer', '1');

      return url.toString();
    }

    async finish() {
      if (!this.collectionUrl) {
        return;
      }

      const navigation = this.navigationId;
      let destination = null;

      if (this.opensSingleResult) {
        this.setAttribute('aria-busy', 'true');
        destination = await this.singleResultUrl();
        this.removeAttribute('aria-busy');

        // The customer may have gone back while the count was in flight.
        if (navigation !== this.navigationId) {
          return;
        }
      }

      window.location.assign(destination || this.buildResultsUrl());
    }
  }

  customElements.define('quiz-funnel', QuizFunnel);
}
